import React, { ComponentProps, FunctionComponent } from "react";

import moment from "moment";

import { motion } from "framer-motion";

import styled from "styled-components";
import { rgba } from "polished";
import ArrowRight from "../../icons/arrow-right";
import { easeTransition } from "../../misc/framer";
import useIsMobile from "../../hooks/is-mobile";
import { useEditMode } from "../../contexts/edit";
import { VisisbilityButtons } from "../edit-controls/visibility-buttons";
import { TracPageFeatureItemType } from "@services/tracpage/types/trac-page-feature-item";
import { SectionIdentifier } from "@services/tracpage/types/trac-page-section-id";
import { ItemsOrderButtons } from "../edit-controls/items-order-buttons";

interface TourItemProps {
  id: string;
  className?: string;
  title: string;
  location: string;
  date: Date;
  isHighlight?: boolean;
  isHidden?: boolean;
  onClick?: () => void;
  orderButtonsDisabledState?: ComponentProps<
    typeof ItemsOrderButtons
  >["disabledState"];
  isMainPage?: boolean;
}

export const TourItemAlt: FunctionComponent<TourItemProps> = ({
  id,
  className,
  title,
  location,
  date,
  isHighlight,
  isHidden = false,
  onClick,
  orderButtonsDisabledState,
  isMainPage = false,
}) => {
  const { editMode } = useEditMode();
  const isMobile = useIsMobile();

  const iconMotion = {
    rest: { opacity: 0, x: 32, right: -32, y: "-50%" },
    hover: {
      opacity: 1,
      x: 0,
      y: "-50%",
      right: 0,
    },
  };

  const textMotion = {
    rest: {
      x: 0,
      transition: {
        ...easeTransition,
      },
    },
    hover: {
      x: -40,
      transition: {
        ...easeTransition,
      },
    },
  };

  return (
    <TourContainer editMode={!isHighlight && editMode}>
      <TourWrapper
        isHidden={isHidden}
        className={`${className || ""} ${isHighlight ? "highlight" : ""}`}
        onClick={onClick}
      >
        <div className="left-content">
          <DateItem className="date-item">
            <h6 className="month">{moment(date).format("MMM")}</h6>
            <h4 className="day">{moment(date).format("DD")}</h4>
          </DateItem>
          <div className="content">
            <h4 className="tour-title">{title}</h4>
            <p className="tour-location">{location}</p>
            {isMobile && !isHighlight && (
              <h3 className="full-date">
                {moment(date).format("ddd, DD MMMM YYYY")}
              </h3>
            )}
          </div>
        </div>
        {!isHighlight ? (
          <div className="right-content">
            {!isMobile ? (
              <motion.h3 className="full-date" variants={textMotion}>
                {moment(date).format("ddd, DD MMMM YYYY")}
              </motion.h3>
            ) : null}
            <motion.div className="arrow-right-circle" variants={iconMotion}>
              <ArrowRight />
            </motion.div>
          </div>
        ) : null}
      </TourWrapper>
      {!isHighlight && (
        <div className="edit-controls">
          <VisisbilityButtons
            disabledState={{ feature: isHidden }}
            itemId={id}
            itemType={TracPageFeatureItemType.TourEvent}
            sectionId={SectionIdentifier.EVENTS}
          />
          {!isHidden && (
            <ItemsOrderButtons
              itemId={id}
              sectionId={SectionIdentifier.EVENTS}
              disabledState={orderButtonsDisabledState}
              forMainPageOrder={isMainPage}
            />
          )}
        </div>
      )}
    </TourContainer>
  );
};

const TourContainer = styled.div<{ editMode: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;

  border-bottom: ${({ editMode, theme }) =>
    editMode ? `1px solid ${rgba(theme.colors.primary, 0.1)}` : "inherit"};
  padding: ${({ editMode }) => (editMode ? "24px 0px" : "0")};

  .edit-controls {
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 24px;
  }
`;

const TourWrapper = styled.div<{ isHidden: boolean }>`
  opacity: ${({ isHidden }) => (isHidden ? 0.5 : 1)};

  position: relative;
  overflow: hidden;

  display: flex;
  flex-direction: row;
  gap: 16px;
  align-items: center;
  justify-content: space-between;
  text-decoration: none;
  width: 100%;
  cursor: ${({ onClick }) => (onClick ? "pointer" : "default")};

  &:focus,
  &:focus-visible,
  &:focus-within {
    outline: none;
  }

  &.highlight {
    padding: 20px;
    border: 1px solid ${({ theme }) => rgba(theme.colors.primary, 0.1)};
    background-color: ${({ theme }) => rgba(theme.colors.primary, 0.01)};

    transition: background-color
        ${({ theme }) =>
          `${theme.transitions.duration} ${theme.transitions.type}`},
      border
        ${({ theme }) =>
          `${theme.transitions.duration} ${theme.transitions.type}`};

    &:hover {
      border: 1px solid ${({ theme }) => rgba(theme.colors.primary, 0.3)};
      background-color: ${({ theme }) => rgba(theme.colors.primary, 0.05)};
    }

    @media (max-width: ${({ theme }) => `${theme.breakpoints.tablet}px`}) {
      border: 1px solid ${({ theme }) => rgba(theme.colors.highlightLight, 0.5)};
      background: ${({ theme }) => rgba(theme.colors.highlightLight, 0.02)};
    }

    .date-item {
      border-radius: 2px;
    }

    .tour-location {
      text-transform: none;
    }
  }

  .edit-controls {
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 24px;
  }

  .left-content {
    display: flex;
    flex-direction: row;
    gap: 16px;
    align-items: center;
    justify-content: flex-start;
  }

  .right-content {
    min-height: 32px;
    position: relative;
    display: flex;
    flex-direction: row;
    gap: 8px;
    align-items: center;
    justify-content: flex-end;

    .arrow-right-circle {
      position: absolute;
      top: 50%;
      right: 0;
      opacity: 0;

      display: flex;
      align-items: center;
      justify-content: center;
      width: 32px;
      height: 32px;
      border-radius: 50%;
      background-color: ${({ theme }) => theme.colors.iconBackground};
    }
  }

  .tour-title,
  .tour-location {
    color: ${({ theme }) => theme.colors.primary};
    margin: 0;
  }

  .tour-title {
    font-size: 16px;
    font-weight: 700;
    line-height: 24px; /* 150% */
  }

  .tour-location {
    color: ${({ theme }) => theme.colors.icon};
    font-family: ${({ theme }) => theme.fonts.UrbaneDemiBold};
    font-size: 14px;
    font-weight: 600;
    line-height: normal;
    text-transform: uppercase;
  }
  .full-date {
    margin: 0;
    color: ${({ theme }) => theme.colors.primary};
    font-family: ${({ theme }) => theme.fonts.UrbaneDemiBold};
    font-size: 14px;
    font-weight: 500;
    line-height: normal;
    text-transform: uppercase;
    @media (max-width: ${({ theme }) => `${theme.breakpoints.tablet}px`}) {
      color: ${({ theme }) => theme.colors.icon};
    }
  }
`;

const DateItem = styled.div`
  display: flex;
  flex-direction: column;
  width: 48px;
  height: 48px;
  padding: 3px 10px;
  justify-content: center;
  align-items: center;
  background-color: ${({ theme }) => theme.colors.white};
  .month,
  .day {
    margin: 0;
    text-align: center;
    color: ${({ theme }) => theme.colors.black};
    font-weight: 600;
    line-height: normal;
    text-transform: uppercase;
    font-family: ${({ theme }) => theme.fonts.UrbaneDemiBold};
  }
  .month {
    font-size: 12px;
  }
  .day {
    font-size: 16px;
  }
`;
